import { useCallback, useMemo, useState } from 'react';
import { Filters, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Package, ShieldOff } from 'lucide-react';

import MemberResourcesTable from '../components/member-tabs/MemberResourcesTable';
import MemberTabEmpty from '../components/member-tabs/MemberTabEmpty';
import MemberTabGuard from '../components/member-tabs/MemberTabGuard';
import RemoveResourceAccessModal from '../components/member-tabs/RemoveResourceAccessModal';
import ResourcesFiltersSlideout from '../components/member-tabs/ResourcesFiltersSlideout';
import { MEMBER_STATUS } from '../constants/copy';
import { RESOURCE_TYPE_ORDER } from '../constants/memberResources';
import { useMemberResources } from '../hooks/useMemberResources';
import { fromSlotValue, toSlotValue } from '../utils/filterAdapters';

/** `Filters` is memoised: a stable constant for what never changes. */
const VIEW_TOGGLE_HIDDEN = Object.freeze({ hidden: true });

/**
 * Resources tab of the member detail: search and a Type filter over the datasets, models, event
 * buses and sandboxes the member reaches, with a confirm before removing direct access.
 *
 * @param {object} props
 * @param {object} props.member UI member (see `mapApiMemberToUi`)
 */
function MemberResourcesTab({ member }) {
  const { t, i18n } = useTranslation('people-access');
  const { resources, isLoading, error, removedAny, removeAccess, reload } = useMemberResources(member);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [removing, setRemoving] = useState(null);

  const handleCloseRemove = useCallback(() => setRemoving(null), []);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [draftType, setDraftType] = useState('');

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (event) => setSearch(event.target.value),
      onClear: () => setSearch(''),
      placeholder: t('memberTabs.resources.searchPlaceholder'),
      'aria-label': t('memberTabs.resources.searchPlaceholder'),
    }),
    [search, t],
  );

  const typeOptions = useMemo(() => {
    const present = new Set(resources.map((r) => r.type));
    return RESOURCE_TYPE_ORDER.filter((value) => present.has(value)).map((value) => ({
      value,
      label: t(`memberTabs.resources.types.${value}`),
    }));
  }, [resources, t]);

  // A type that is no longer offered (its last resource was removed) stops filtering.
  const activeType = useMemo(() => (typeOptions.some((o) => o.value === type) ? type : ''), [typeOptions, type]);

  const slots = useMemo(
    () => [
      {
        id: 'type',
        label: t('memberTabs.resources.typeFilter'),
        multiple: false,
        value: toSlotValue(activeType),
        onChange: (next) => setType(fromSlotValue(next)),
        options: typeOptions,
      },
    ],
    [t, activeType, typeOptions],
  );
  const filtersI18n = useMemo(
    () => ({
      clearFilters: t('members.filters.clear'),
      allFilters: activeType ? t('members.filters.allFiltersCount', { count: 1 }) : t('members.filters.allFilters'),
    }),
    [t, activeType],
  );

  const handleClearAll = useCallback(() => {
    setSearch('');
    setType('');
  }, []);
  const handleOpenFilters = useCallback(() => {
    setDraftType(activeType);
    setIsFiltersOpen(true);
  }, [activeType]);
  const handleCloseFilters = useCallback(() => setIsFiltersOpen(false), []);
  const handleApplyFilters = useCallback(() => setType(draftType), [draftType]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return resources.filter(
      (r) => (!activeType || r.type === activeType) && (!needle || r.name.toLowerCase().includes(needle)),
    );
  }, [resources, search, activeType]);

  const noMatch = useMemo(
    () => (
      <StatusMessage
        icon={Package}
        title={t('memberTabs.resources.noMatchTitle')}
        subtitle={t('memberTabs.resources.noMatchSubtitle')}
      />
    ),
    [t],
  );

  const sourceLabel = removing
    ? t(`memberTabs.resources.sources.${removing.source}`, { via: removing.via })
    : '';
  const dateLabel = removing?.grantedAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(removing.grantedAt))
    : '';

  return (
    <MemberTabGuard
      tabId="resources"
      icon={Package}
      isRemoved={member.status === MEMBER_STATUS.REMOVED}
      isLoading={isLoading}
      error={error}
      onRetry={reload}
    >
      {resources.length === 0 ? (
        <MemberTabEmpty
          icon={removedAny ? ShieldOff : Package}
          title={t(removedAny ? 'memberTabs.resources.emptyAfterTitle' : 'memberTabs.resources.emptyTitle')}
          subtitle={t(removedAny ? 'memberTabs.resources.emptyAfterSubtitle' : 'memberTabs.resources.emptySubtitle')}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <Filters
            searchProps={searchProps}
            filters={slots}
            viewToggle={VIEW_TOGGLE_HIDDEN}
            onClearAll={handleClearAll}
            onAllFiltersClick={handleOpenFilters}
            i18n={filtersI18n}
          />
          <MemberResourcesTable
            resources={visible}
            locale={i18n.language}
            onRemove={setRemoving}
            emptyMessage={noMatch}
          />
        </div>
      )}
      <ResourcesFiltersSlideout
        isOpen={isFiltersOpen}
        onClose={handleCloseFilters}
        draftType={draftType}
        onDraftTypeChange={setDraftType}
        typeOptions={typeOptions}
        onApply={handleApplyFilters}
      />
      <RemoveResourceAccessModal
        resource={removing}
        memberName={member.displayName}
        typeLabel={removing ? t(`memberTabs.resources.types.${removing.type}`) : ''}
        sourceLabel={sourceLabel}
        dateLabel={dateLabel}
        onClose={handleCloseRemove}
        onConfirm={removeAccess}
      />
    </MemberTabGuard>
  );
}

export default MemberResourcesTab;
